/**
 * MultiLinkChipRow — Iter 418.bf (Dirk, 2026-02-28)
 *
 * Renders a many-to-many link row using the /api/entity-links pipeline.
 * Shows chips for every currently-linked target, each with a remove
 * button, plus an "Add" button that opens the appropriate picker.
 *
 * Props:
 *   entityKind   - "ticket" | "jobcard"
 *   entityId     - id of the entity we're viewing
 *   customerId   - required so pickers list only that customer's items
 *   targetKind   - "jobcard" | "ticket" | "sales_doc"
 *   targetIds    - array of currently-linked ids (from the entity doc)
 *   canEdit      - if false, chips render but no add/remove buttons
 *   label        - row label
 *   onChanged    - callback fired after successful add/remove; parent
 *                  re-fetches to pick up the fresh array + family tree
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, X as XIcon, FileText, LifeBuoy, Receipt } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import JobCardPickerModal from "./JobCardPickerModal";
import TicketPickerModal from "./TicketPickerModal";
import SalesOrderPickerModal from "./SalesOrderPickerModal";

const TARGET_META = {
  jobcard:   { addLabel: "Add job card…", tone: "emerald", Icon: FileText, prefix: "JC", empty: "No job cards linked.", route: "/jobcards" },
  ticket:    { addLabel: "Add ticket…",   tone: "rose",    Icon: LifeBuoy, prefix: "ST", empty: "No tickets linked.",  route: "/tickets" },
  sales_doc: { addLabel: "Add sales order…", tone: "cyan", Icon: Receipt,  prefix: "SO", empty: "No sales orders linked.", route: "/sales-documents" },
};

// Iter 418.bf — backend entity-links uses "sales_doc" but URLs use "sales-documents"
// and target list endpoints vary. TARGET_LIST_URL builds the right lookup call.
const TARGET_LIST_URL = {
  jobcard:   (cid) => `/jobcards?customer_id=${cid}`,
  ticket:    (cid) => `/tickets?customer_id=${cid}`,
  sales_doc: (cid) => `/sales-documents?customer_id=${cid}&category=sales_order`,
};

export default function MultiLinkChipRow({
  entityKind,
  entityId,
  customerId,
  targetKind,
  targetIds = [],
  canEdit = false,
  label,
  onChanged,
}) {
  const meta = TARGET_META[targetKind];
  const [dict, setDict] = useState({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const ids = useMemo(() => (Array.isArray(targetIds) ? targetIds : []).filter(Boolean), [targetIds]);

  // Hydrate id → doc so we can render short_id + title on the chip.
  useEffect(() => {
    if (!customerId || ids.length === 0) return;
    const url = TARGET_LIST_URL[targetKind]?.(customerId);
    if (!url) return;
    let alive = true;
    api.get(url).then(({ data }) => {
      if (!alive) return;
      const rows = Array.isArray(data) ? data : (data?.items || []);
      const d = {};
      rows.forEach((r) => { d[r.id] = r; });
      setDict(d);
    }).catch(() => {});
    return () => { alive = false; };
  }, [customerId, targetKind, ids.length]);

  const connect = async (targetId) => {
    try {
      await api.post("/entity-links/connect", {
        a_type: entityKind, a_id: entityId,
        b_type: targetKind, b_id: targetId,
      });
      toast.success(`Linked ${meta.prefix}`);
      setPickerOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not link.");
    }
  };

  const disconnect = async (targetId) => {
    if (!window.confirm(`Unlink this ${targetKind.replace("_", " ")}?`)) return;
    try {
      await api.post("/entity-links/disconnect", {
        a_type: entityKind, a_id: entityId,
        b_type: targetKind, b_id: targetId,
      });
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not unlink.");
    }
  };

  const PickerModal = targetKind === "jobcard"
    ? JobCardPickerModal
    : targetKind === "ticket"
      ? TicketPickerModal
      : SalesOrderPickerModal;

  const Icon = meta.Icon;
  const toneClasses = {
    emerald: { chip: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:border-emerald-400", label: "text-emerald-300" },
    rose:    { chip: "border-rose-500/40 bg-rose-500/10 text-rose-200 hover:border-rose-400", label: "text-rose-300" },
    cyan:    { chip: "border-cyan-500/40 bg-cyan-500/10 text-cyan-200 hover:border-cyan-400", label: "text-cyan-300" },
  }[meta.tone];

  const chipsById = ids.map((id) => {
    const doc = dict[id];
    // Iter 427.p46 — When the target doc can't be resolved from the
    // customer's list (deleted / different category / stale link),
    // we mark the chip as `orphan` so it renders in a warning tone,
    // is NOT clickable (avoids the "black screen" from routing to a
    // missing doc), and always shows the remove X.
    const orphan = !doc;
    const short = doc?.short_id || doc?.document_number || id.slice(0, 8);
    const shortDisp = short.toUpperCase().startsWith(meta.prefix) ? short : `${meta.prefix}${short}`;
    const title = doc?.title || doc?.subject || doc?.file_name || (orphan ? "(deleted or unavailable)" : "");
    return { id, shortDisp, title, orphan };
  });

  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-[#647087]">{label}</label>
      <div className="mt-1 flex flex-wrap gap-2 items-center">
        {chipsById.length === 0 && (
          <div className="text-[13px] text-[#a0aabf] italic">{meta.empty}</div>
        )}
        {chipsById.map((c) => (
          <div
            key={c.id}
            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border transition ${c.orphan ? "border-amber-500/40 bg-amber-500/10 text-amber-200" : toneClasses.chip}`}
            data-testid={c.orphan ? `link-chip-orphan-${targetKind}-${c.id.slice(0, 8)}` : undefined}
            title={c.orphan ? "This link points to a deleted or unavailable record. Click the ✕ to remove it." : undefined}
          >
            <Icon className="w-3 h-3" />
            {c.orphan ? (
              <span
                className={`font-mono text-xs font-semibold text-amber-300`}
                data-testid={`link-chip-${targetKind}-${c.id.slice(0, 8)}`}
              >
                {c.shortDisp}
              </span>
            ) : (
              <Link
                to={`${meta.route}/${c.id}`}
                className={`font-mono text-xs font-semibold ${toneClasses.label}`}
                data-testid={`link-chip-${targetKind}-${c.id.slice(0, 8)}`}
              >
                {c.shortDisp}
              </Link>
            )}
            {c.title && <span className="text-xs text-white/80 max-w-[220px] truncate">{c.title}</span>}
            {(canEdit || c.orphan) && (
              <button
                type="button"
                onClick={() => disconnect(c.id)}
                className="ml-1 opacity-70 hover:opacity-100 rounded hover:bg-white/10 p-0.5"
                title="Unlink"
                data-testid={`link-chip-remove-${targetKind}-${c.id.slice(0, 8)}`}
              >
                <XIcon className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}
        {canEdit && (
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border border-dashed border-white/20 text-xs text-[#a0aabf] hover:text-white hover:border-white/40 transition`}
            data-testid={`link-add-${targetKind}`}
          >
            <Plus className="w-3 h-3" /> {meta.addLabel}
          </button>
        )}
      </div>
      <PickerModal
        open={pickerOpen}
        customerId={customerId}
        excludeIds={ids}
        onPick={(doc) => connect(doc.id)}
        onClose={() => setPickerOpen(false)}
      />
    </div>
  );
}
